import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  useModalStackStore,
  PreviewCardMapData,
  EventModalData,
  ModalEntry
} from '../stores/modalStackStore';
import { Event } from '../types/event.types';

// 🔧 FIX: Flag pour éviter le double-traitement popstate
// Quand closeTopModal/closeAll appellent history.back()/go(),
// le popstate résultant ne doit PAS re-synchroniser la stack (déjà fait)
let _isProgrammaticBack = false;

/**
 * 🔄 useModalStack - Hook centralisé pour la navigation modale
 *
 * Phase 4 PRD Navigation Back Intelligente
 *
 * Ce hook gère :
 * - La synchronisation bidirectionnelle entre la stack et history
 * - L'ouverture/fermeture des modales (PreviewCardMap, EventModal)
 * - La gestion du bouton back navigateur via popstate
 *
 * Flux supportés :
 * - Vue Map : HomePage → PreviewCardMap → EventModal → back → PreviewCardMap → back → HomePage
 * - Vue Liste : HomePage → EventModal → back → HomePage
 * - Multi-events : Event1 → Event2 → Event3 → back → HomePage (sans repasser par chaque event)
 */

export interface UseModalStackReturn {
  // State (via selectors optimisés)
  stack: ModalEntry[];
  currentModal: ModalEntry | null;
  isPreviewCardMapOpen: boolean;
  isEventModalOpen: boolean;
  previewCardMapData: PreviewCardMapData | null;
  eventModalData: EventModalData | null;

  // Actions
  openPreviewCardMap: (data: Omit<PreviewCardMapData, 'isMapView'>, location: string) => void;
  openEventModal: (event: Event, options?: OpenEventModalOptions) => void;
  closeTopModal: () => void;
  closeAll: () => void;

  // Debug
  debugStack: () => void;
}

interface OpenEventModalOptions {
  seriesEvents?: Event[];
  fromPreviewCardMap?: boolean;
  isMapView?: boolean;
  userLocation?: { lat: number; lng: number } | null;
}

export const useModalStack = (): UseModalStackReturn => {
  // 🔧 FIX PERF: useShallow pour éviter re-renders quand seul _idCounter change
  // Sans sélecteur, chaque changement du store (y compris _idCounter) causait un re-render
  const { _push, _pop, _clear, _replaceTop, _syncWithUrl, stack } = useModalStackStore(
    useShallow((state) => ({
      _push: state._push,
      _pop: state._pop,
      _clear: state._clear,
      _replaceTop: state._replaceTop,
      _syncWithUrl: state._syncWithUrl,
      stack: state.stack
    }))
  );

  // 🔧 FIX: Dériver TOUTES les valeurs depuis la même source (stack)
  // Cela garantit qu'elles sont toujours synchronisées
  const currentModal = useMemo(() =>
    stack.length > 0 ? stack[stack.length - 1] : null,
    [stack]
  );

  const isPreviewCardMapOpen = useMemo(() =>
    stack.some(m => m.type === 'previewCardMap'),
    [stack]
  );

  const isEventModalOpen = useMemo(() =>
    currentModal?.type === 'eventModal',
    [currentModal]
  );

  const previewCardMapData = useMemo(() => {
    const entry = stack.find(m => m.type === 'previewCardMap');
    return entry ? entry.data as PreviewCardMapData : null;
  }, [stack]);

  const eventModalData = useMemo(() =>
    currentModal?.type === 'eventModal' ? currentModal.data as EventModalData : null,
    [currentModal]
  );

  // 🔧 DEBUG: Log pour tracer les changements de stack
  useEffect(() => {
    console.log('🔄 [useModalStack] Stack changed:', {
      stackSize: stack.length,
      isPreviewCardMapOpen,
      isEventModalOpen,
      types: stack.map(m => m.type)
    });
  }, [stack, isPreviewCardMapOpen, isEventModalOpen]);

  // Ref pour tracking de l'URL (debug)
  const lastUrlRef = useRef(window.location.pathname);

  // ============================================================================
  // SYNC AVEC HISTORY (popstate)
  // ============================================================================

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const currentUrl = window.location.pathname;

      // 🔧 FIX: Si c'est un back/go programmatique (closeTopModal/closeAll),
      // la stack est déjà à jour, on skip la synchronisation
      if (_isProgrammaticBack) {
        console.log('🔙 [useModalStack] popstate programmatique → skip sync');
        _isProgrammaticBack = false;
        lastUrlRef.current = currentUrl;
        return;
      }

      console.log('🔙 [useModalStack] popstate détecté:', {
        previousUrl: lastUrlRef.current,
        currentUrl,
        stackSize: stack.length,
        historyState: event.state
      });

      // 🔧 FIX PHASE 4: Toujours synchroniser la stack avec l'URL
      _syncWithUrl(currentUrl);

      lastUrlRef.current = currentUrl;
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [stack.length, _syncWithUrl]);

  // 🔧 FIX PHASE 4 v3: Synchronisation continue avec l'URL
  // Détecte les changements d'URL qui ne passent pas par popstate (ex: navigate() de Wouter)
  useEffect(() => {
    const currentUrl = window.location.pathname;

    // Si l'URL est '/' mais la stack n'est pas vide, synchroniser
    if ((currentUrl === '/' || currentUrl === '') && stack.length > 0) {
      console.log('🔗 [useModalStack] Détection désync: URL=/ mais stack non vide, synchronisation...');
      _syncWithUrl(currentUrl);
    }

    lastUrlRef.current = currentUrl;
  }, [stack.length, _syncWithUrl]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Ouvre PreviewCardMap (vue multi-événements depuis la carte)
   *
   * 🔧 FIX PHASE 4: Comportement intelligent :
   * - Si une PreviewCardMap est déjà au top → REPLACE (pas d'ajout à l'historique)
   * - Sinon → PUSH (ajout à l'historique)
   */
  const openPreviewCardMap = useCallback((
    data: Omit<PreviewCardMapData, 'isMapView'>,
    location: string
  ) => {
    const url = `/events/${encodeURIComponent(location)}`;
    const fullData: PreviewCardMapData = {
      ...data,
      location,
      isMapView: true
    };

    const topModal = useModalStackStore.getState().stack.slice(-1)[0];
    const isChangingPreviewCard = topModal?.type === 'previewCardMap';

    console.log('📍 [useModalStack] openPreviewCardMap:', {
      location,
      eventsCount: data.events.length,
      url,
      isChangingPreviewCard
    });

    if (isChangingPreviewCard) {
      // REPLACE : Une PreviewCardMap est déjà ouverte, on la remplace
      _replaceTop({
        type: 'previewCardMap',
        data: fullData,
        url
      });
      window.history.replaceState({ modalType: 'previewCardMap', location }, '', url);
    } else {
      // PUSH : Première ouverture de PreviewCardMap
      _push({
        type: 'previewCardMap',
        data: fullData,
        url
      });
      window.history.pushState({ modalType: 'previewCardMap', location }, '', url);
    }

    lastUrlRef.current = url;
  }, [_push, _replaceTop]);

  /**
   * Ouvre EventModal
   *
   * Comportement intelligent :
   * - Si on est déjà sur un EventModal → REPLACE (pas d'ajout à l'historique)
   * - Sinon → PUSH (ajout à l'historique)
   */
  const openEventModal = useCallback((
    event: Event,
    options: OpenEventModalOptions = {}
  ) => {
    const url = `/event/${event.id}`;
    const topModal = useModalStackStore.getState().stack.slice(-1)[0];
    const isChangingEvent = topModal?.type === 'eventModal';

    // Déterminer si on vient de PreviewCardMap
    const fromPreviewCardMap = options.fromPreviewCardMap ??
      useModalStackStore.getState().stack.some(m => m.type === 'previewCardMap');

    const modalData: EventModalData = {
      event,
      seriesEvents: options.seriesEvents,
      fromPreviewCardMap,
      isMapView: options.isMapView ?? fromPreviewCardMap,
      userLocation: options.userLocation
    };

    console.log('🎭 [useModalStack] openEventModal:', {
      eventId: event.id,
      isChangingEvent,
      fromPreviewCardMap,
      url
    });

    if (isChangingEvent) {
      // REPLACE : On change d'event sans ajouter à l'historique
      // Ceci permet de faire : Event1 → Event2 → Event3 → back → HomePage/PreviewCardMap
      _replaceTop({
        type: 'eventModal',
        data: modalData,
        url
      });
      window.history.replaceState({ modalType: 'eventModal', eventId: event.id }, '', url);
    } else {
      // PUSH : Nouvelle modale
      _push({
        type: 'eventModal',
        data: modalData,
        url
      });
      window.history.pushState({ modalType: 'eventModal', eventId: event.id }, '', url);
    }

    lastUrlRef.current = url;
  }, [_push, _replaceTop]);

  /**
   * Ferme la modale du haut de la stack
   *
   * 🔧 FIX v3: Utilise history.back() au lieu de replaceState
   * replaceState laissait des entrées "fantômes" dans l'historique :
   * chaque open/close ajoutait une entrée invisible, empêchant le bouton
   * retour de quitter l'app depuis la homepage.
   * history.back() supprime proprement l'entrée créée par le pushState d'ouverture.
   */
  const closeTopModal = useCallback(() => {
    const currentStack = useModalStackStore.getState().stack;
    const stackSize = currentStack.length;

    if (stackSize === 0) {
      console.log('❌ [useModalStack] closeTopModal: Stack vide');
      return;
    }

    const topModal = currentStack[stackSize - 1];
    const previousModal = stackSize > 1 ? currentStack[stackSize - 2] : null;

    console.log('❌ [useModalStack] closeTopModal:', {
      stackSize,
      closingType: topModal.type,
      closingUrl: topModal.url,
      targetType: previousModal?.type || 'home'
    });

    // Pop la stack AVANT history.back()
    _pop();

    // Flag pour que le popstate handler ne re-synchronise pas (déjà fait)
    _isProgrammaticBack = true;
    window.history.back();

    lastUrlRef.current = previousModal ? previousModal.url : '/';

    console.log('✅ [useModalStack] closeTopModal terminé:', {
      newStackSize: useModalStackStore.getState().stack.length
    });
  }, [_pop]);

  /**
   * Ferme toutes les modales et retourne à la home
   *
   * 🔧 FIX v3: Utilise history.go(-n) au lieu de pushState
   * Chaque modale ouverte par PUSH a créé une entrée d'historique.
   * On revient en arrière du nombre exact d'entrées pour tout nettoyer.
   */
  const closeAll = useCallback(() => {
    const stackSize = useModalStackStore.getState().stack.length;

    if (stackSize === 0) return;

    console.log('🧹 [useModalStack] closeAll:', { stackSize });

    // Vider la stack
    _clear();

    // Revenir en arrière du nombre d'entrées dans la stack
    // (chaque push a créé 1 entrée, les replace n'en créent pas mais ne changent pas la taille)
    _isProgrammaticBack = true;
    window.history.go(-stackSize);
    lastUrlRef.current = '/';
  }, [_clear]);

  /**
   * Debug : affiche la stack dans la console
   */
  const debugStack = useCallback(() => {
    const currentStack = useModalStackStore.getState().stack;
    console.log('📚 [useModalStack] Debug Stack:');
    console.table(currentStack.map(m => ({
      id: m.id,
      type: m.type,
      url: m.url,
      timestamp: new Date(m.timestamp).toISOString()
    })));
  }, []);

  return {
    stack,
    currentModal,
    isPreviewCardMapOpen,
    isEventModalOpen,
    previewCardMapData,
    eventModalData,
    openPreviewCardMap,
    openEventModal,
    closeTopModal,
    closeAll,
    debugStack
  };
};

// ============================================================================
// HOOK SIMPLIFIÉ POUR COMPOSANTS QUI N'ONT BESOIN QUE DE FERMER
// ============================================================================

/**
 * Hook léger pour les composants qui ont juste besoin de fermer une modale
 */
export const useCloseModal = () => {
  const closeTopModal = useCallback(() => {
    window.history.back();
  }, []);

  return closeTopModal;
};

// ============================================================================
// UTILITAIRE : Initialisation depuis URL directe
// ============================================================================

/**
 * Initialise la stack si on arrive sur une URL directe (lien partagé)
 * À appeler une seule fois au montage de l'app
 */
export const initializeFromUrl = async (
  getEventById: (id: string) => Promise<Event | null>
) => {
  const path = window.location.pathname;
  const store = useModalStackStore.getState();

  // Si stack non vide, déjà initialisé
  if (store.stack.length > 0) return;

  // /event/:id → Ouvrir EventModal directement
  const eventMatch = path.match(/^\/event\/([^/]+)$/);
  if (eventMatch) {
    const eventId = eventMatch[1];
    console.log('🔗 [useModalStack] Initialisation depuis URL /event/', eventId);

    try {
      const event = await getEventById(eventId);
      if (event) {
        store._push({
          type: 'eventModal',
          data: {
            event,
            fromPreviewCardMap: false,
            isMapView: false
          },
          url: path
        });
      }
    } catch (error) {
      console.error('❌ [useModalStack] Erreur chargement event:', error);
    }
    return;
  }

  // /events/:location → On ne peut pas restaurer sans les events
  // L'utilisateur devra naviguer normalement
  const eventsMatch = path.match(/^\/events\/([^/]+)$/);
  if (eventsMatch) {
    console.log('🔗 [useModalStack] URL /events/ détectée, redirection vers home');
    // Rediriger vers home car on n'a pas les données
    window.history.replaceState(null, '', '/');
  }
};
